/**
 * CookieConsentEs - GDPR/CCPA compliant cookie consent banner
 * Spanish version
 *
 * @module components/CookieConsent
 * @description Spanish localization of the cookie consent banner
 */

import React, { useState, useEffect, useCallback } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { hasConsentBeenShown, setAnalyticsConsent } from "@/lib/analytics";

interface CookieConsentProps {
  /** Callback fired when user accepts cookies */
  onAccept?: () => void;
  /** Callback fired when user declines cookies */
  onDecline?: () => void;
  /** Delay before showing banner (ms) - default 1500 */
  showDelay?: number;
}

const CookieConsentEs: React.FC<CookieConsentProps> = ({
  onAccept,
  onDecline,
  showDelay = 1500,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Don't show if consent decision has already been made
    if (hasConsentBeenShown()) return;

    // Delay to not impact LCP
    const timer = setTimeout(() => setIsVisible(true), showDelay);
    return () => clearTimeout(timer);
  }, [showDelay]);

  const handleAccept = useCallback(() => {
    setAnalyticsConsent(true);
    setIsVisible(false);
    onAccept?.();
  }, [onAccept]);

  const handleDecline = useCallback(() => {
    setAnalyticsConsent(false);
    setIsVisible(false);
    onDecline?.();
  }, [onDecline]);

  return (
    <AnimatePresence>
      {isVisible && (
        <m.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 380, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-consent-title"
          aria-describedby="cookie-consent-description"
        >
          <div className="mx-auto max-w-4xl bg-dark-800 border border-dark-700 rounded-xl shadow-2xl shadow-dark-950/50 p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Content */}
              <div className="flex-1">
                <h3
                  id="cookie-consent-title"
                  className="text-lg font-semibold text-dark-50 mb-2"
                >
                  Su Privacidad Importa
                </h3>
                <p
                  id="cookie-consent-description"
                  className="text-sm text-dark-300 leading-relaxed"
                >
                  Este sitio utiliza cookies y tecnologías similares para
                  analizar el tráfico, entender cómo lo usa y mejorar su
                  experiencia. Sus datos me ayudan a servirle mejor.{" "}
                  <Link
                    to="/es/politica-de-privacidad"
                    className="text-metro-blue-500 hover:text-metro-blue-400 underline transition-colors"
                  >
                    Más información en nuestra Política de Privacidad
                  </Link>
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:flex-shrink-0">
                <button
                  type="button"
                  onClick={handleDecline}
                  className="px-5 py-2.5 text-sm font-medium text-dark-300 
                           bg-dark-700 hover:bg-dark-600 border border-dark-600 
                           rounded-lg transition-colors duration-200
                           focus:outline-none focus:ring-2 focus:ring-metro-orange-500 
                           focus:ring-offset-2 focus:ring-offset-dark-800"
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  className="px-5 py-2.5 text-sm font-semibold text-dark-950 
                           bg-metro-orange-500 hover:bg-metro-orange-400 
                           rounded-lg transition-colors duration-200
                           focus:outline-none focus:ring-2 focus:ring-metro-orange-500 
                           focus:ring-offset-2 focus:ring-offset-dark-800"
                >
                  Aceptar Todo
                </button>
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentEs;
